import { SyntheticEvent, useEffect, useState } from 'react';
import { colors } from '../../constants';
import { SensorTypes, ThresholdType } from '../../models/commonEnums';
import { ThresholdRange } from '../../models/commonTypeScript';
import { CustomOptions, ThresholdOption } from '../../models/customThresholdModels';

export type UseThresholdSliderProps = {
    thresholdOptions: ThresholdOption[];
    customOptions?: CustomOptions;
    setSelectedValues: (values: number[]) => void;
    selectedValues: number[];
    sensorType: SensorTypes;
};

type UseThresholdSliderReturn = {
    determineColorValue: (thresholdType: ThresholdType) => string;
    isValidNumberInput: (index: number) => boolean;
    normalizeRanges: (range: ThresholdRange[], min: number, max: number) => ThresholdRange[];
    updateInputValue: (event: SyntheticEvent<HTMLInputElement>, index: number) => void;
    sliderConfig: {
        min: number;
        max: number;
        step: number;
    };
    thumbColors: string[];
};
const useThresholdSlider = ({
    thresholdOptions,
    customOptions,
    setSelectedValues,
    selectedValues,
    sensorType,
}: UseThresholdSliderProps): UseThresholdSliderReturn => {
    const [thumbColors, setThumbColors] = useState<string[]>([]);
    const [sliderConfig] = useState({
        min: customOptions?.minSelectableValue || 0,
        max: customOptions?.maxSelectableValue || 100,
        step: customOptions?.thresholdDelta || 1,
    });

    const determineColorValue = (thresholdType: ThresholdType): string => {
        return [ThresholdType.LOW_ALERT, ThresholdType.HIGH_ALERT].includes(thresholdType)
            ? colors.graphRed
            : colors.yellowGlow;
    };

    useEffect(() => {
        if (thresholdOptions.length === 0) return;
        const color = thresholdOptions?.map(option => determineColorValue(option.type));
        setThumbColors(color);
        setSelectedValues(thresholdOptions.map(option => option.value));
    }, [thresholdOptions]);

    const isValidNumberInput = (index: number): boolean => {
        const currentValue = selectedValues[index];
        const previousValue = selectedValues[index - 1];
        const nextValue = selectedValues[index + 1];

        return (
            currentValue !== undefined &&
            (previousValue === undefined || currentValue > previousValue) &&
            (nextValue === undefined || currentValue < nextValue)
        );
    };
    const normalizeRanges = (range: ThresholdRange[], min: number, max: number): ThresholdRange[] => {
        return range?.map(r => ({
            from: r.from !== undefined ? r.from : min,
            to: r.to !== undefined ? r.to : max,
            rating: r.rating,
        }));
    };
    const allowedDecimals =
        sensorType === SensorTypes.temp || (customOptions?.thresholdDelta && customOptions?.thresholdDelta < 1) ? 1 : 0;

    const numberOfDecimals = (numericValue: number, value: string): number =>
        Math.floor(numericValue) === numericValue ? 0 : value.toString().split('.')[1].length || 0;

    const updateInputValue = (event: SyntheticEvent<HTMLInputElement>, index: number): void => {
        const { value } = event.currentTarget;
        const numericValue = Number(value);
        if (
            Number.isNaN(numericValue) ||
            (allowedDecimals > 0 && numberOfDecimals(numericValue, value) > allowedDecimals)
        ) {
            return;
        }
        const updatedValues = [...selectedValues];
        if (numericValue < sliderConfig.min || numericValue > sliderConfig.max) return;
        updatedValues[index] = allowedDecimals === 0 ? Number(numericValue.toFixed(0)) : numericValue;
        setSelectedValues(updatedValues);
    };

    return {
        determineColorValue,
        isValidNumberInput,
        normalizeRanges,
        updateInputValue,
        sliderConfig,
        thumbColors,
    };
};

export default useThresholdSlider;
